import React, { useState } from 'react';
import FileUploadArea from '../FileUploadArea';
import ImportPreviewTable from '../ImportPreviewTable';
import ProgressIndicator from '../ProgressIndicator';
import { api } from '../../utils/api';

export interface ImportResult {
  totalShifts: number;
  newShifts: number;
  updatedShifts: number;
  importDate: string;
  importId: string;
}

interface BulkImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (result: ImportResult) => void;
}

type ImportState = 'upload' | 'validating' | 'preview' | 'importing' | 'success' | 'error';

interface PreviewRow {
  nip: string;
  employeeName: string;
  date: string;
  scheduleType: string;
  isUpdate: boolean;
  oldValue?: string;
}

interface ValidationError {
  rowNumber: number;
  errorDescription: string;
  nip?: string;
  date?: string;
  scheduleType?: string;
}

export const BulkImportModal: React.FC<BulkImportModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [state, setState] = useState<ImportState>('upload');
  const [uploadId, setUploadId] = useState<string>('');
  const [previewData, setPreviewData] = useState<PreviewRow[]>([]);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [validRows, setValidRows] = useState<number>(0);
  const [errorRows, setErrorRows] = useState<number>(0);
  const [conflictCount, setConflictCount] = useState<number>(0);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  if (!isOpen) return null;

  const handleFileSelect = async (file: File) => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      setState('validating');
      setStatusMessage('File diterima, memvalidasi data...');

      // Upload file
      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await api.post('/shifts/import/upload', formData);

      if (!uploadResponse.success) {
        throw new Error(uploadResponse.data?.error || 'Upload failed');
      }

      const { uploadId: newUploadId } = uploadResponse.data;
      setUploadId(newUploadId);

      // Validate file
      const validateResponse = await api.post('/shifts/import/validate', {
        uploadId: newUploadId
      });

      if (!validateResponse.success) {
        setErrorMessage(validateResponse.data?.error || 'Validation failed');
        setErrors(validateResponse.data?.errors || []);
        setErrorRows(validateResponse.data?.errorRows || 0);
        setState('error');
        setStatusMessage('');
        return;
      }

      const { validRows: newValidRows, errorRows: newErrorRows, preview, conflictCount: newConflictCount, errors: validationErrors } = validateResponse.data;

      setValidRows(newValidRows);
      setErrorRows(newErrorRows);
      setPreviewData(preview);
      setConflictCount(newConflictCount);
      setErrors(validationErrors || []); // Set errors even if success
      setStatusMessage(`Validasi selesai: ${newValidRows} baris valid, ${newErrorRows} baris error`);

      if (newErrorRows > 0) {
        setState('error');
      } else {
        setState('preview');
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Upload failed');
      setState('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      setState('importing');
      setStatusMessage('Importing...');
      setProgress(0);

      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + Math.random() * 30, 90));
      }, 500);

      const confirmResponse = await api.post('/shifts/import/confirm', {
        uploadId,
        confirmConflicts: true
      });

      clearInterval(progressInterval);
      setProgress(100);

      if (!confirmResponse.success) {
        throw new Error(confirmResponse.data?.error || 'Import failed');
      }

      const result: ImportResult = {
        totalShifts: confirmResponse.data.totalShifts,
        newShifts: confirmResponse.data.newShifts,
        updatedShifts: confirmResponse.data.updatedShifts,
        importDate: confirmResponse.data.importDate,
        importId: confirmResponse.data.importId
      };

      setImportResult(result);
      setState('success');
      setStatusMessage('Import berhasil!');

      if (onSuccess) {
        onSuccess(result);
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Import failed');
      setState('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setState('upload');
    setUploadId('');
    setPreviewData([]);
    setErrors([]);
    setValidRows(0);
    setErrorRows(0);
    setConflictCount(0);
    setProgress(0);
    setStatusMessage('');
    setErrorMessage('');
  };

  const handleClose = () => {
    handleCancel();
    onClose();
  };

  const handleRetry = () => {
    handleCancel();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-full w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold">Bulk Import Shift Roster</h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {state === 'upload' && (
            <FileUploadArea
              onFileSelect={handleFileSelect}
              isLoading={isLoading}
              error={errorMessage}
            />
          )}

          {state === 'validating' && (
            <div className="text-center py-8">
              <div className="inline-block">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              </div>
              <p className="mt-4 text-gray-600">{statusMessage}</p>
            </div>
          )}

          {state === 'preview' && (
            <div>
              {conflictCount > 0 && (
                <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-yellow-800">
                    ⚠️ Anda akan mengupdate {conflictCount} shift yang sudah ada. Lanjutkan?
                  </p>
                </div>
              )}
              <ImportPreviewTable
                data={previewData}
                totalRows={validRows}
                validRows={validRows}
                errorRows={errorRows}
                onConfirm={handleConfirmImport}
                onCancel={handleCancel}
                isLoading={isLoading}
              />
            </div>
          )}

          {state === 'importing' && (
            <div>
              <ProgressIndicator
                current={Math.floor(progress)}
                total={100}
                stage="importing"
              />
              <p className="mt-4 text-center text-gray-600">{statusMessage}</p>
              {progress > 30 && (
                <p className="mt-2 text-center text-orange-600 text-sm">
                  ℹ️ Proses import sedang berjalan, jangan tutup halaman ini
                </p>
              )}
            </div>
          )}

          {state === 'success' && importResult && (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">✓</div>
              <h3 className="text-lg font-bold text-green-600 mb-4">Import Berhasil!</h3>
              <div className="bg-gray-50 p-4 rounded mb-4 text-left">
                <p className="mb-2"><strong>Total Shift:</strong> {importResult.totalShifts}</p>
                <p className="mb-2"><strong>Shift Baru:</strong> {importResult.newShifts}</p>
                <p className="mb-2"><strong>Shift Diupdate:</strong> {importResult.updatedShifts}</p>
                <p><strong>Tanggal Import:</strong> {new Date(importResult.importDate).toLocaleString()}</p>
              </div>
              <button
                onClick={handleClose}
                className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600"
              >
                Tutup
              </button>
            </div>
          )}

          {state === 'error' && (
            <div>
              {/* Main Error Message */}
              <div className="mb-6 p-6 bg-red-50 border-2 border-red-300 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="text-2xl">❌</div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-red-800 mb-2">Validasi Gagal</h3>
                    <p className="text-red-700 mb-3">{errorMessage}</p>
                    {errorRows > 0 && (
                      <p className="text-sm text-red-600">
                        <strong>Total Error:</strong> {errorRows} baris memiliki masalah
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Error Details */}
              {errors && errors.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-3">Detail Error ({errors.length})</h3>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto">
                    <div className="space-y-2">
                      {errors.slice(0, 20).map((error, idx) => (
                        <div key={idx} className="p-3 bg-white border-l-4 border-red-400 rounded">
                          <p className="text-sm font-semibold text-gray-800">
                            Baris {error.rowNumber}
                          </p>
                          <p className="text-sm text-red-700 mt-1">
                            {error.errorDescription}
                          </p>
                          {error.nip && (
                            <p className="text-xs text-gray-600 mt-1">
                              NIP: {error.nip}
                            </p>
                          )}
                          {error.date && (
                            <p className="text-xs text-gray-600">
                              Tanggal: {error.date}
                            </p>
                          )}
                          {error.scheduleType && (
                            <p className="text-xs text-gray-600">
                              Schedule Type: {error.scheduleType}
                            </p>
                          )}
                        </div>
                      ))}
                      {errors.length > 20 && (
                        <p className="text-sm text-gray-600 p-2 bg-yellow-50 rounded border border-yellow-200">
                          ... dan {errors.length - 20} error lainnya. Download laporan untuk melihat semua error.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleRetry}
                  className="flex-1 bg-blue-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-600 transition-colors"
                >
                  Upload File Baru
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkImportModal;
